export const DEFAULT_FORM_COMPLIANCE_SORT = {
  key: 'complianceRate',
  direction: 'ascending',
};

export const toComplianceNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const calculateComplianceRate = (completed, total) => {
  const normalizedTotal = toComplianceNumber(total);
  if (normalizedTotal <= 0) return null;

  return (toComplianceNumber(completed) / normalizedTotal) * 100;
};

export const formatComplianceRate = (value) => (
  Number.isFinite(value) ? `${value.toFixed(1)}%` : 'N/A'
);

export const isComplianceApplicable = (row = {}) => (
  toComplianceNumber(row.totalSurveys ?? row.total_surveys ?? row.total) > 0
);

export const calculateComplianceSummary = (rows = [], threshold = 80) => {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const applicableRows = normalizedRows.filter(isComplianceApplicable);
  const totalCompletedSurveys = applicableRows.reduce(
    (acc, row) => acc + toComplianceNumber(row.completedSurveys ?? row.completed_surveys ?? row.completed),
    0
  );
  const totalSurveys = applicableRows.reduce(
    (acc, row) => acc + toComplianceNumber(row.totalSurveys ?? row.total_surveys ?? row.total),
    0
  );
  const complianceRate = calculateComplianceRate(totalCompletedSurveys, totalSurveys);
  const rowRates = applicableRows.map((row) => {
    const providedRate = row.complianceRate ?? row.compliance_rate;
    if (Number.isFinite(providedRate)) return providedRate;

    return calculateComplianceRate(
      row.completedSurveys ?? row.completed_surveys ?? row.completed,
      row.totalSurveys ?? row.total_surveys ?? row.total
    );
  }).filter(Number.isFinite);
  const averageCompliance = rowRates.length > 0
    ? rowRates.reduce((acc, rate) => acc + rate, 0) / rowRates.length
    : null;
  const compliantRows = rowRates.filter((rate) => rate >= threshold).length;
  const nonCompliantRows = applicableRows.length - compliantRows;

  return {
    totalRows: normalizedRows.length,
    applicableRows: applicableRows.length,
    compliantRows,
    nonCompliantRows,
    totalParticipants: normalizedRows.length,
    applicableParticipants: applicableRows.length,
    compliantParticipants: compliantRows,
    nonCompliantParticipants: nonCompliantRows,
    complianceRate,
    averageCompliance,
    totalCompletedSurveys,
    totalSurveys,
  };
};

export const normalizeFormComplianceRows = (forms = []) => {
  if (!Array.isArray(forms)) return [];

  return forms.map((form) => {
    const completedSurveys = Number(form.completedSurveys ?? form.completed_surveys ?? form.completed ?? 0);
    const missedSurveys = Number(form.missedSurveys ?? form.missed_surveys ?? form.missed ?? 0);
    const inProgressSurveys = Number(form.inProgressSurveys ?? form.in_progress_surveys ?? form.in_progress ?? 0);
    const pendingSurveys = Number(form.pendingSurveys ?? form.pending_surveys ?? form.pending ?? 0);
    const totalSurveys = Number(form.totalSurveys ?? form.total_surveys ?? form.total ?? completedSurveys + missedSurveys);
    const isApplicable = totalSurveys > 0;
    const complianceRate = isApplicable
      ? Number(form.complianceRate ?? form.compliance_rate ?? calculateComplianceRate(completedSurveys, totalSurveys))
      : null;
    const missedRate = isApplicable
      ? Number(form.missedRate ?? form.missed_rate ?? ((missedSurveys / totalSurveys) * 100))
      : null;

    return {
      formId: form.formId ?? form.form_id ?? form.surveyId ?? form.survey_id ?? form.id,
      formName: form.formName ?? form.form_name ?? form.surveyName ?? form.survey_name ?? form.name ?? 'Unnamed Form',
      scheduleType: form.scheduleType ?? form.schedule_type ?? 'Scheduled',
      scheduleTypes: form.scheduleTypes ?? form.schedule_types ?? [],
      totalSurveys,
      completedSurveys,
      missedSurveys,
      inProgressSurveys,
      pendingSurveys,
      assignedParticipants: Number(form.assignedParticipants ?? form.assigned_participants ?? 0),
      totalWindows: Number(form.totalWindows ?? form.total_windows ?? totalSurveys),
      complianceRate,
      missedRate,
      isComplianceApplicable: isApplicable,
      siteBreakdown: (form.siteBreakdown ?? form.site_breakdown ?? []).map((site) => {
        const siteCompleted = Number(site.completedSurveys ?? site.completed_surveys ?? site.completed ?? 0);
        const siteMissed = Number(site.missedSurveys ?? site.missed_surveys ?? site.missed ?? 0);
        const siteInProgress = Number(site.inProgressSurveys ?? site.in_progress_surveys ?? site.in_progress ?? 0);
        const sitePending = Number(site.pendingSurveys ?? site.pending_surveys ?? site.pending ?? 0);
        const siteTotal = Number(site.totalSurveys ?? site.total_surveys ?? site.total ?? siteCompleted + siteMissed);
        const siteApplicable = siteTotal > 0;

        return {
          ...site,
          siteId: site.siteId ?? site.site_id ?? site.id,
          siteName: site.siteName ?? site.site_name ?? site.name,
          completedSurveys: siteCompleted,
          missedSurveys: siteMissed,
          inProgressSurveys: siteInProgress,
          pendingSurveys: sitePending,
          totalSurveys: siteTotal,
          assignedParticipants: Number(site.assignedParticipants ?? site.assigned_participants ?? 0),
          complianceRate: siteApplicable
            ? Number(site.complianceRate ?? site.compliance_rate ?? calculateComplianceRate(siteCompleted, siteTotal))
            : null,
          isComplianceApplicable: siteApplicable,
        };
      }),
    };
  });
};

export const sortFormComplianceRows = (
  forms,
  sortConfig = DEFAULT_FORM_COMPLIANCE_SORT
) => {
  const { key, direction } = sortConfig || DEFAULT_FORM_COMPLIANCE_SORT;
  const multiplier = direction === 'descending' ? -1 : 1;

  return [...normalizeFormComplianceRows(forms)].sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];

    if (!Number.isFinite(aValue) && Number.isFinite(bValue)) return 1;
    if (Number.isFinite(aValue) && !Number.isFinite(bValue)) return -1;

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * multiplier;
    }

    return String(aValue ?? '').localeCompare(String(bValue ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    }) * multiplier;
  });
};

export const filterFormComplianceRows = (forms, searchTerm = '') => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) return normalizeFormComplianceRows(forms);

  return normalizeFormComplianceRows(forms).filter((form) => (
    form.formName.toLowerCase().includes(normalizedSearchTerm) ||
    form.scheduleType.toLowerCase().includes(normalizedSearchTerm)
  ));
};
